<template>
  <CircleSpinner v-if="loading || kiosksLoading" />
  <RouterView v-else :event="event" :kiosk-id="props.kioskId" :product-categories="productCategories" :kiosk="kiosk" />

  <ConfirmModal
    v-if="showNavigationDialog"
    :title="$t('FestivalShop.confirmNavigation.title')"
    :description="$t('FestivalShop.confirmNavigation.description')"
    :cancel="$t('FestivalShop.confirmNavigation.leave')"
    :approve="$t('FestivalShop.confirmNavigation.stay')"
    wrapper-class="rounded-lg"
    @close-dialog="handleNavigationConfirmationDialog"
  />
</template>

<script setup lang="ts">
import { CircleSpinner } from '@ramp106/omrjs-core-ui'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'

import ConfirmModal from '@/components/ConfirmModal.vue'
import { useFestivalShopCart } from '@/composables/useFestivalShopCart'
import { EventPeriodEnum, useFestivalShopKiosksQuery, useFestivalShopProductCategoriesQuery, type Event } from '@/gql/myomr'
import { globalizeUrlifiedId } from '@/helpers/urlifyGlobalId'
import { useFestivalShopStore } from '@/stores/festivalShop'

const props = defineProps<{
  event: Event
  kioskId: string
}>()

const route = useRoute()
const router = useRouter()

const showNavigationDialog = ref(false)
const pendingNavigation = ref<(() => void) | null>(null)

const kioskGlobalId = computed(() => globalizeUrlifiedId(props.kioskId))
const eventSlug = computed(() => props.event?.slug ?? null)
const queryVars = computed(() => ({
  kioskId: kioskGlobalId.value,
}))
const queryOptions = computed(() => ({
  enabled: eventSlug.value != null,
}))
const { loading, result } = useFestivalShopProductCategoriesQuery(queryVars, queryOptions)
const festivalShopCart = useFestivalShopCart()
const { cartItemCount, clearCart } = festivalShopCart

const productCategories = computed(() => {
  return result.value?.festivalShopProductCategories.nodes ?? []
})

const kiosksPeriod = computed(() => (route.query.period === 'past' ? EventPeriodEnum.Past : EventPeriodEnum.Upcoming))
const { result: kiosksResult, loading: kiosksLoading } = useFestivalShopKiosksQuery(() => ({ period: kiosksPeriod.value, limit: 30 }))
const festivalShopStore = useFestivalShopStore()

const kiosks = computed(() => {
  return kiosksResult.value?.festivalShopKiosks.nodes ?? []
})

const kiosk = computed(() => {
  return kiosksResult.value?.festivalShopKiosks.nodes.filter((k) => k.id == kioskGlobalId.value).at(0) ?? null
})

const handleNavigationConfirmationDialog = (confirmed: boolean) => {
  showNavigationDialog.value = false
  if (confirmed && pendingNavigation.value) {
    clearCart()
    pendingNavigation.value()
  }
  pendingNavigation.value = null
}

onBeforeRouteUpdate((to) => {
  if (to.params['kioskId'] != props.kioskId) {
    if (!cartItemCount.value) return true

    showNavigationDialog.value = true
    pendingNavigation.value = () => {
      router.push(to)
    }

    return false
  }
  return true
})

watch(
  kiosks,
  (currentKiosks) => {
    festivalShopStore.setAvailableKiosks(currentKiosks)
  },
  { immediate: true },
)

watch(
  kiosk,
  (currentKiosk) => {
    festivalShopStore.setCurrentKiosk(currentKiosk)
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  festivalShopStore.setAvailableKiosks([])
  festivalShopStore.setCurrentKiosk(null)
})
</script>
